import Vue from 'vue'
import {ActionTree, MutationTree} from 'vuex'
import {RootState} from '@/store/types'
import axios from 'axios';
import router from "@/router";

export interface Tokens {
    access_token: string
    refresh_token: string
}

export interface State {
    isRefresh: any,
    user: Object,
    tokens: Tokens
    exhibitorId: string | null
    loadStatus: string
    userCompanyId: string | null
    showId: string | null
    eventId: string | null
    registrationRequired: boolean
}

const defaultState: State = {
    isRefresh: false,
    user: {},
    tokens: {
        access_token: localStorage.getItem('access-token') || '',
        refresh_token: localStorage.getItem('access-token') || ''
    },
    exhibitorId: null,
    loadStatus: '',
    userCompanyId: null,
    showId: null,
    eventId: null,
    registrationRequired: false
}

const getters = {
    isRefresh: (state: State) => state.isRefresh,
    user: (state: State) => state.user,
    isAuthenticated: (state: State) => !!state.tokens.access_token,
    status: (state: State) => state.loadStatus,
    showId: (state: State) => state.showId,
    registrationRequired: (state: State) => state.registrationRequired
}

const mutations: MutationTree<State> = {
    setRegistrationRequired(state: State, payload: boolean) {
        state.registrationRequired = payload
    },
    setUser(state: State, payload: any) {
        state.user = payload
    },
    loading(state: State) {
        state.loadStatus = 'loading'
    },

    tokenSetup(state: State, newTokens: Tokens) {
        state.loadStatus = 'success'
        state.tokens.access_token = newTokens.access_token
        // state.tokens.refresh_token = newTokens.refresh_token
        Vue.axios.defaults.headers.common['Authorization'] = 'Bearer ' + newTokens.access_token
    },

    exhibitorSetup(state: State, exhibitorId: string) {
        state.exhibitorId = exhibitorId
    },
    userSetup(state: State, userObj: Object) {
        state.user = userObj
    },

    userCompanySetup(state: State, companyId: string) {
        state.userCompanyId = companyId
    },

    showSetup(state: State, showId: string) {
        state.showId = showId
    },

    eventSetup(state: State, eventId: string) {
        state.eventId = eventId
    },

    error(state: State) {
        state.loadStatus = 'error'
        state.tokens.access_token = ''
        state.tokens.refresh_token = ''
        state.exhibitorId = null
        state.showId = null
        state.eventId = null
    },

    clear(state: State) {
        state.loadStatus = ''
        state.tokens.access_token = ''
        state.tokens.refresh_token = ''
        state.exhibitorId = null
        state.showId = null
        state.eventId = null
        localStorage.removeItem('access-token')
        localStorage.removeItem('refresh-token')
        localStorage.removeItem('exhibitor')
        localStorage.removeItem('userCompanyId')
        localStorage.removeItem('showId')
        localStorage.removeItem('eventId')
        delete Vue.axios.defaults.headers.common['Authorization']
        router.push({name: 'Auth'}).catch(() => {
        })
    }
}

const actions: ActionTree<State, RootState> = {
    async checkLogin({commit, state}, payload: any) {
        try {
            const {data} = await axios.post('v5/auth/admin/check-login', payload)
            commit('setRegistrationRequired', data.data.registrationRequired)
        } catch (e) {
            this.commit('alerts/setAlert', {
                show: true,
                messages: [e.message]
            })
            commit('setRegistrationRequired', true)

        }
    },
    async getConfirmCode({commit, state}, payload) {
        try {
            console.log(payload)
            await axios.post('v5/auth/confirm-code/send', payload)
        } catch (e) {
            this.commit('alerts/setAlert', {
                show: true,
                messages: [e.message]
            })
        }
    },
    async logIn({commit, state}, payload) {

        return new Promise((resolve, reject) => {
            localStorage.removeItem('access-token')
            localStorage.removeItem('refresh-token')

            Vue.axios({
                    url: 'v5/auth/login',
                    method: 'POST',
                    data: payload,
                })
                .then((resp: any) => {
                    if (resp.data.data.access && resp.data.data.refresh) {
                        let access_token = resp.data.data.access
                        let refresh_token = resp.data.data.refresh
                        localStorage.setItem('access-token', access_token)
                        localStorage.setItem('refresh-token', refresh_token)
                        commit('tokenSetup', {access_token, refresh_token})

                        resolve({
                            access_token: resp.data.access,
                            refresh_token: resp.data.refresh,
                            data: resp.data
                        })
                    } else {
                        reject(resp)
                    }
                })
                .catch((err: any) => {
                    localStorage.removeItem('access-token')
                    localStorage.removeItem('refresh-token')
                    reject(err)
                })
        })

    },


    logout({commit}) {
        commit('clear')
    },

    restoreFromLS({commit}) {
        return new Promise((resolve, reject) => {
            const access_token = localStorage.getItem('access-token')
            const refresh_token = localStorage.getItem('refresh-token')
            const exhibitor = localStorage.getItem('exhibitor')
            const userCompanyId = localStorage.getItem('userCompanyId')
            const showId = localStorage.getItem('showId')
            const eventId = localStorage.getItem('eventId')

            if (access_token && refresh_token && exhibitor && userCompanyId && showId && eventId) {
                commit('tokenSetup', {access_token, refresh_token})
                commit('exhibitorSetup', exhibitor)
                commit('userCompanySetup', userCompanyId)
                commit('showSetup', showId)
                commit('eventSetup', eventId)
                resolve({access_token, refresh_token, exhibitor, userCompanyId, showId, eventId})
            } else {
                commit('clear')
                localStorage.removeItem('access-token')
                localStorage.removeItem('refresh-token')
                localStorage.removeItem('exhibitor')
                localStorage.removeItem('userCompanyId')
                localStorage.removeItem('showId')
                localStorage.removeItem('eventId')
                reject()
            }
        })
    },

    async refresh({commit}) {
        try {
            if (!localStorage.getItem('refresh-token')) return false
            const {data} = await axios.post('v5/auth/token/refresh',
                {refresh: localStorage.getItem('refresh-token')})
            localStorage.setItem('access-token', data.data.access);
            localStorage.setItem('refresh-token', data.data.refresh);
            commit('tokenSetup', {access_token: data.data.access, refresh_token: data.data.refresh});
            return true
        } catch (e) {
            return false
        } finally {

        }


    },
    async refreshUser({commit}) {
        try {
            const {data} = await axios.get('v5/user')
            if (data.status === 'err') {
                throw (data.error)
            } else {
                commit('setUser', data.data.user)
            }

        } catch (e) {
            console.log(e)
        }


    }


}

export default {
    namespaced: true,
    state: defaultState,
    getters,
    mutations,
    actions,
};
